import React from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { useGetOrgQuery } from "../../../services/OrgService";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { EditPencilSvg } from "../../../components/svg/EditPencilSvg";

import { Typography } from "../../../components/typography/TypographyComponents";
import { AiSettingsCard } from "../../ai/AiSettingsCard";
import { TwoStepFirmCard } from "../../twoStep/TwoStepFirmCard";
import { regionName, regionOf } from "../../region/region";
import { useIsFirmAdmin } from "../../region/useFirmRegion";
import { CountryCard } from "./CountryCard";

export const CompanyDetail: React.FC = () => {
  const navigate = useNavigate();

  const { data: org, isLoading: isLoadingOrg } = useGetOrgQuery(
    getLinkedFirm()?.orgId || "",
  );

  const isAdmin = useIsFirmAdmin();

  if (isLoadingOrg) {
    return <LottieLoading />;
  }

  const address = org?.locations?.[0]?.locationAddress;
  const region = regionOf(address?.country);

  const editOrgInfo = (
    <Link
      to={"./edit"}
      className={
        "bg-purple flex items-center justify-center rounded-[7px] px-[20px] py-[8px] text-[14px] text-white"
      }
    >
      <div>Edit</div>
    </Link>
  );

  return (
    <>
      <div className="mb-[120px] py-6">
        <div className="mb-6 flex items-center justify-between gap-4 font-Manrope">
          <Typography fontcss="subtitle2" colorcss="bodyprimary">
            Firm Details
          </Typography>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/settings/company/edit")}
              className="flex h-10 items-center justify-center gap-2 rounded-lg border border-[#E4E9F2] bg-white px-3 py-2 text-sm font-medium text-[#1D2C3B] shadow-sm hover:bg-[#F9FAFB]"
            >
              <EditPencilSvg />
              <span>Edit</span>
            </button>
          </div>
        </div>

        <CountryCard country={region.code} isAdmin={isAdmin} />

        <div className="mt-6 flex items-start justify-between rounded-xl border border-[#E4E9F2] bg-white p-6 max-md:flex-col max-md:gap-2 max-md:p-4">
          <div className="w-[42%] max-md:w-full">
            <Typography fontcss="subtitle3" colorcss="bodyprimary">
              General Details
            </Typography>
            <Typography fontcss="cardSub" colorcss="bodytertiary">
              Basic information about this company
            </Typography>
          </div>
          <div className="grid flex-1 grid-cols-2 gap-x-8 max-md:w-full max-md:grid-cols-1 max-md:gap-x-0 max-md:[overflow-wrap:anywhere]">
            <div className="py-3">
              <Typography fontcss="body2Regular" colorcss="bodytertiary">
                Company Name
              </Typography>
              <Typography fontcss="body2Medium" colorcss="bodyprimary">
                {org?.legalName}
              </Typography>
            </div>
            <div className="py-3">
              <Typography fontcss="body2Regular" colorcss="bodytertiary">
                Invoice Display Name
              </Typography>
              <Typography fontcss="body2Medium" colorcss="bodyprimary">
                {org?.displayName}
              </Typography>
            </div>
            <div className="py-3">
              <Typography fontcss="body2Regular" colorcss="bodytertiary">
                Email
              </Typography>
              <Typography fontcss="body2Medium" colorcss="bodyprimary">
                {org?.email}
              </Typography>
            </div>
            <div className="py-3">
              <Typography fontcss="body2Regular" colorcss="bodytertiary">
                Phone Number
              </Typography>
              <Typography fontcss="body2Medium" colorcss="bodyprimary">
                {org?.phoneNumbers?.[0]?.number}
              </Typography>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-start justify-between rounded-xl border border-[#E4E9F2] bg-white p-6 max-md:flex-col max-md:gap-2 max-md:p-4">
          <div className="w-[42%] max-md:w-full">
            <Typography fontcss="subtitle3" colorcss="bodyprimary">
              Address
            </Typography>
            <Typography fontcss="cardSub" colorcss="bodytertiary">
              Company address information
            </Typography>
          </div>
          <div className="grid flex-1 grid-cols-2 gap-x-8 max-md:w-full max-md:grid-cols-1 max-md:gap-x-0 max-md:[overflow-wrap:anywhere]">
            <div className="col-span-2 py-3 max-md:col-span-1">
              <Typography fontcss="body2Regular" colorcss="bodytertiary">
                Address
              </Typography>
              <Typography fontcss="body2Medium" colorcss="bodyprimary">
                {address?.line1}
              </Typography>
            </div>
            <div className="py-3">
              <Typography fontcss="body2Regular" colorcss="bodytertiary">
                {region.cityLabel}
              </Typography>
              <Typography fontcss="body2Medium" colorcss="bodyprimary">
                {address?.city}
              </Typography>
            </div>
            <div className="py-3">
              <Typography fontcss="body2Regular" colorcss="bodytertiary">
                {region.regionLabel}
              </Typography>
              <Typography fontcss="body2Medium" colorcss="bodyprimary">
                {regionName(region, address?.region)}
              </Typography>
            </div>
            <div className="py-3">
              <Typography fontcss="body2Regular" colorcss="bodytertiary">
                {region.postalLabel}
              </Typography>
              <Typography fontcss="body2Medium" colorcss="bodyprimary">
                {address?.postalCode}
              </Typography>
            </div>
          </div>
        </div>
        <AiSettingsCard />
        <TwoStepFirmCard />
      </div>
    </>
  );
};
